import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { COMMON } from '@locales/id/messages';
import { Button } from './Button';
import { Dialog } from './Dialog';

export interface ConfirmOpts { title: string; body?: ReactNode; confirmLabel: string; danger?: boolean; cancelLabel?: string }

const ConfirmCtx = createContext<(o: ConfirmOpts) => Promise<boolean>>(async () => false);

/** `await confirm({...})` instead of window.confirm: styled, keyboard friendly, Indonesian. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [req, setReq] = useState<ConfirmOpts | null>(null);
  const pending = useRef<((v: boolean) => void) | null>(null);
  const ask = useCallback(
    (o: ConfirmOpts) =>
      new Promise<boolean>((resolve) => {
        pending.current?.(false); // a newer question replaces an open one: its caller gets "no", never a hang
        pending.current = resolve;
        setReq(o);
      }),
    [],
  );
  const done = (v: boolean) => {
    pending.current?.(v);
    pending.current = null;
    setReq(null);
  };
  return (
    <ConfirmCtx.Provider value={ask}>
      {children}
      {req && (
        <Dialog
          title={req.title}
          onClose={() => done(false)}
          footer={
            <>
              {/* a destructive question starts on Batal, so a stray Enter does not delete anything */}
              <Button onClick={() => done(false)} data-autofocus={req.danger || undefined}>{req.cancelLabel ?? COMMON.cancel}</Button>
              <Button variant={req.danger ? 'danger' : 'primary'} onClick={() => done(true)} data-autofocus={!req.danger || undefined}>
                {req.confirmLabel}
              </Button>
            </>
          }
        >
          {req.body && <div className="confirm-body">{req.body}</div>}
        </Dialog>
      )}
    </ConfirmCtx.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmCtx);
